import { useCallback, useMemo } from 'react';
import {
  useGetActiveSchoolContextQuery,
  useListTenantSchoolsQuery,
  useSwitchSchoolContextMutation,
} from './domainRouterApi';
import {
  deriveSchoolContextState,
  type SchoolContextState,
} from './schoolContextState';
import { useShellProfile } from './useShellProfile';
export type { TenantSchool } from './schoolContextState';

const readQueryError = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const message = 'message' in value ? String((value as { message?: unknown }).message ?? '').trim() : '';
  return message || null;
};

export function useSchoolContext(): SchoolContextState {
  const { effectivePersona } = useShellProfile();
  const canUseSchoolContext =
    effectivePersona === 'staff'
    || effectivePersona === 'administrator'
    || effectivePersona === 'super-administrator';
  const queryOptions = { skip: !canUseSchoolContext };
  const tenantSchoolsQuery = useListTenantSchoolsQuery(undefined, queryOptions);
  const activeSchoolContextQuery = useGetActiveSchoolContextQuery(undefined, queryOptions);
  const [switchSchoolContextMutation, switchSchoolContextState] =
    useSwitchSchoolContextMutation();

  const selectSchool = useCallback(
    async (schoolId: string) => {
      await switchSchoolContextMutation({ schoolId }).unwrap();
    },
    [switchSchoolContextMutation],
  );

  const state = useMemo(
    () =>
      deriveSchoolContextState({
        canUseSchoolContext,
        activeSchoolId: activeSchoolContextQuery.data?.schoolId ?? null,
        schools: tenantSchoolsQuery.data?.schools ?? null,
        isLoadingQueries:
          tenantSchoolsQuery.isLoading || activeSchoolContextQuery.isLoading,
        isSwitching: switchSchoolContextState.isLoading,
        error:
          readQueryError(activeSchoolContextQuery.error)
          ?? readQueryError(tenantSchoolsQuery.error)
          ?? readQueryError(switchSchoolContextState.error),
      }),
    [
      activeSchoolContextQuery.data?.schoolId,
      activeSchoolContextQuery.error,
      activeSchoolContextQuery.isLoading,
      canUseSchoolContext,
      switchSchoolContextState.error,
      switchSchoolContextState.isLoading,
      tenantSchoolsQuery.data?.schools,
      tenantSchoolsQuery.error,
      tenantSchoolsQuery.isLoading,
    ],
  );

  return {
    ...state,
    selectSchool,
  };
}
